import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchStudents as fetchStudentsList, commitStudentUpdates } from '../../services/studentService';
import SendPortalInvite from '../../components/SendPortalInvite/SendPortalInvite';
import { filterAvailableStudents } from '../../utils/studentFilters';
import { fetchAssignedTimeSlots } from '../../services/timeSlotService';
import { fetchAllInvites, indexInvites, portalAccessFor, portalAccessDetail } from '../../services/portalInviteService';
import { indexSlotsByStudent, withScheduleFields, legacyNextLessonDate } from '../../utils/studentSchedule';
import { useTestMode } from '../../contexts/TestModeContext';
import TestModeBanner from '../../components/TestMode/TestModeBanner';
import PortalHeader from '../../components/Portal/PortalHeader';
import AccessNotice from '../../components/Portal/AccessNotice';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { hasPerm, isPermissionDenied } from '../../utils/permissions';
import '../../components/Portal/portalSurface.css';
import './Students.css';

const COLUMN_LABELS = {
	name: 'Name',
	email: 'Email',
	phone: 'Phone',
	school: 'School',
	status: 'Status',
	portalAccess: 'Portal Access',
	nextLesson: 'Next Lesson',
	lessonDay: 'Lesson Day',
	age: 'Age',
	instrument: 'Instrument',
	parent: 'Parent Contact',
};

// nextLesson is a calendar day, and the UTC-midnight coercion behind it (#14)
// now lives in utils/studentSchedule.js so the roster and the Student Profile
// cannot drift on it. Rows arrive already decorated by withScheduleFields —
// canonical timeSlots first, legacy `students.nextLesson` only as a fallback.
const nextLessonDate = legacyNextLessonDate;

const Students = () => {
	const navigate = useNavigate();
	const { testMode } = useTestMode();
	const { userProfile } = useAuth();
	const canSendEmail = hasPerm(userProfile, 'sendEmail');
	const [permDenied, setPermDenied] = useState(false);
	const [students, setStudents] = useState([]);
	const [filteredStudents, setFilteredStudents] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [loading, setLoading] = useState(true);
	const [sortConfig, setSortConfig] = useState({ key: 'fullName', direction: 'asc' });
	const [statusFilter, setStatusFilter] = useState('active'); // Default to active students only
	const [dayFilter, setDayFilter] = useState('all');
	const [studentTypeFilter, setStudentTypeFilter] = useState('all');
	const [visibleColumns, setVisibleColumns] = useState({
		name: true,
		email: true,
		phone: true,
		school: true,
		status: true,
		portalAccess: true,
		nextLesson: true,
		lessonDay: true,
		age: false,
		instrument: false,
		parent: false,
	});
	const [selectedStudents, setSelectedStudents] = useState([]);
	const [selectAll, setSelectAll] = useState(false);
	const [showBatchModal, setShowBatchModal] = useState(false);
	const [batchAction, setBatchAction] = useState('');
	const [showPortalInvite, setShowPortalInvite] = useState(false);

	// Roster rows + their canonical placements. `timeSlots` is the source of
	// truth for the fall; the legacy `students.nextLesson` field only fills in
	// for students who have not been placed in the grid yet.
	const loadDecoratedStudents = async () => {
		const [rows, slots, invites] = await Promise.all([
			fetchStudentsList(),
			// NON-FATAL, and this is load-bearing rather than defensive noise.
			// The timeSlots read is gated on `viewSchedule`, the roster on
			// `viewStudents`, and they are independently togglable in the admin
			// permissions UI. Without this catch a teacher holding viewStudents
			// but not viewSchedule would have the denial bubble out of
			// Promise.all and hit the isPermissionDenied branch below, losing
			// the ENTIRE roster they are entitled to read over a decoration
			// they are not. Falling back to [] degrades to the legacy
			// `nextLesson` derivation — exactly the pre-existing behavior.
			fetchAssignedTimeSlots().catch((err) => {
				console.warn('Lesson-day decoration unavailable (timeSlots read denied):', err);
				return [];
			}),
			// Same non-fatal treatment, one collection read for the whole table
			// (see portalInviteService.fetchAllInvites). NOTE the failure value
			// is `null`, NOT `[]`: an empty array is indistinguishable from "no
			// invites exist" and would paint every row "Not invited", which is
			// a false statement about 26 families who were in fact invited.
			// `null` means unknown, and the cell says so.
			fetchAllInvites().catch((err) => {
				console.warn('Portal-access column unavailable (portalInvites read denied):', err);
				return null;
			}),
		]);
		const byStudent = indexSlotsByStudent(slots);
		const inviteIndex = invites ? indexInvites(invites) : null;
		const now = new Date();
		return rows.map((data) => {
			const row = withScheduleFields(data, byStudent.get(data.id) || []);
			row.portalAccess = inviteIndex
				? portalAccessFor({ studentId: data.id, familyId: data.familyId }, inviteIndex, now)
				: null;
			return row;
		});
	};

	// Get badge style for each day using brand colors
	const getDayBadgeStyle = (day) => {
		const styles = {
			'Monday': { backgroundColor: '#384971', color: '#fff' },      // Base color (dark blue)
			'Tuesday': { backgroundColor: '#82c6c6', color: '#000' },     // Primary blue
			'Wednesday': { backgroundColor: '#ffb953', color: '#000' },   // Primary yellow
			'Thursday': { backgroundColor: '#ed7749', color: '#fff' },    // Primary orange
			'Friday': { backgroundColor: '#f0a8c0', color: '#000' },      // Primary pink
			'Saturday': { backgroundColor: '#fdc0ae', color: '#000' },    // Light pink
			'Sunday': { backgroundColor: '#f933a4', color: '#fff' },      // Headline pink
		};
		return styles[day] || { backgroundColor: '#6c757d', color: '#fff' };
	};

	useEffect(() => {
		const loadStudents = async () => {
			try {
				const studentsData = await loadDecoratedStudents();
				setStudents(studentsData);
				setFilteredStudents(studentsData);
			} catch (error) {
				console.error('Error fetching students:', error);
				// A rules denial (a teacher without viewStudents) must not read as
				// "No students registered yet" — surface it as an access state (#27).
				if (isPermissionDenied(error)) setPermDenied(true);
			} finally {
				setLoading(false);
			}
		};

		loadStudents();
	}, []);

	// Sorting function
	const sortStudents = (studentsToSort) => {
		if (!sortConfig.key) return studentsToSort;

		const sorted = [...studentsToSort].sort((a, b) => {
			let aVal = a[sortConfig.key];
			let bVal = b[sortConfig.key];

			// Handle nested properties
			if (sortConfig.key === 'fullName') {
				aVal = a.fullName || `${a.firstName} ${a.lastName}`;
				bVal = b.fullName || `${b.firstName} ${b.lastName}`;
			}

			// Handle lesson day sorting (Monday first)
			if (sortConfig.key === 'lessonDay') {
				const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
				const aIndex = aVal ? dayOrder.indexOf(aVal) : 999;
				const bIndex = bVal ? dayOrder.indexOf(bVal) : 999;
				return sortConfig.direction === 'asc'
					? aIndex - bIndex
					: bIndex - aIndex;
			}

			// Portal access sorts by RANK, not by label. It needs its own branch
			// ahead of the generic null guard below for a specific reason: the
			// most urgent state ("Not invited") has rank 0, and `if (!aVal)` would
			// read that 0 as missing and sweep exactly those rows to the bottom
			// in both directions — silently burying the ones this column exists
			// to surface. An unknown state (invites unreadable) sorts last.
			if (sortConfig.key === 'portalAccess') {
				const rank = (s) => (s.portalAccess ? s.portalAccess.rank : 99);
				const diff = rank(a) - rank(b);
				if (diff !== 0) return sortConfig.direction === 'asc' ? diff : -diff;
				const an = a.fullName || `${a.firstName} ${a.lastName}`;
				const bn = b.fullName || `${b.firstName} ${b.lastName}`;
				return an.localeCompare(bn);
			}

			// Handle null/undefined
			if (!aVal) return 1;
			if (!bVal) return -1;

			// Compare
			if (typeof aVal === 'string') {
				return sortConfig.direction === 'asc'
					? aVal.localeCompare(bVal)
					: bVal.localeCompare(aVal);
			} else {
				return sortConfig.direction === 'asc'
					? aVal > bVal ? 1 : -1
					: bVal > aVal ? 1 : -1;
			}
		});

		return sorted;
	};

	const handleSort = (key) => {
		setSortConfig((prev) => ({
			key,
			direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
		}));
	};

	const toggleColumn = (column) => {
		setVisibleColumns((prev) => ({
			...prev,
			[column]: !prev[column],
		}));
	};

	// Checkbox selection handlers
	const handleSelectAll = () => {
		if (selectAll) {
			setSelectedStudents([]);
			setSelectAll(false);
		} else {
			setSelectedStudents(filteredStudents.map(s => s.id));
			setSelectAll(true);
		}
	};

	const handleSelectStudent = (studentId) => {
		setSelectedStudents(prev => {
			if (prev.includes(studentId)) {
				return prev.filter(id => id !== studentId);
			} else {
				return [...prev, studentId];
			}
		});
	};

	// Update select all when filtered students change
	useEffect(() => {
		if (selectedStudents.length > 0 && selectedStudents.length === filteredStudents.length) {
			setSelectAll(true);
		} else {
			setSelectAll(false);
		}
	}, [selectedStudents, filteredStudents]);

	// Batch action handlers
	const handleBatchAction = async () => {
		if (!batchAction || selectedStudents.length === 0) return;

		try {
			// Compute the per-student field updates (same skip rules as before),
			// then apply them as independent writes so one denied doc doesn't sink
			// the rest (#17). Track client-side skips for the summary.
			const updates = [];
			const skipped = []; // { name, reason }
			const nameFor = (id) => (students.find(s => s.id === id)?.fullName) || id;

			selectedStudents.forEach(studentId => {
				const studentData = students.find(s => s.id === studentId);

				// Skip locked students for certain operations
				if (studentData?.locked && ['markActive', 'markInactive', 'archive', 'delete'].includes(batchAction)) {
					skipped.push({ name: studentData.fullName || studentId, reason: 'locked' });
					return;
				}

				// Skip archiving students with outstanding balances
				if (batchAction === 'archive' && studentData?.accountBalance && studentData.accountBalance < 0) {
					skipped.push({ name: studentData.fullName || studentId, reason: 'outstanding balance' });
					return;
				}

				let data = null;
				switch(batchAction) {
					case 'markActive':
						data = { status: 'Active', archived: false };
						break;
					case 'markInactive':
						data = { status: 'Inactive' };
						break;
					case 'markReal':
						data = { isTestStudent: false };
						break;
					case 'markTest':
						data = { isTestStudent: true };
						break;
					case 'lock':
						data = { locked: true };
						break;
					case 'unlock':
						data = { locked: false };
						break;
					case 'archive':
						// Can only archive inactive students with no balance
						if (studentData?.status === 'Inactive' && (studentData?.accountBalance || 0) >= 0) {
							data = { archived: true };
						}
						break;
					case 'unarchive':
						data = { archived: false };
						break;
					default:
						break;
				}

				if (data) updates.push({ id: studentId, data });
				else if (batchAction === 'archive') {
					// Passed the balance gate but isn't inactive → not archivable yet.
					skipped.push({ name: studentData?.fullName || studentId, reason: 'must be inactive to archive' });
				}
			});

			const { succeeded, failed } = await commitStudentUpdates(updates);

			// Refresh student list
			const studentsData = await loadDecoratedStudents();
			setStudents(studentsData);
			setFilteredStudents(studentsData);

			// Reset selections
			setSelectedStudents([]);
			setSelectAll(false);
			setShowBatchModal(false);
			setBatchAction('');

			// Warm, accurate summary — partial success is reported, never a blanket
			// "success" or a wholesale failure (#17).
			const lines = [`Updated ${succeeded.length} student${succeeded.length === 1 ? '' : 's'}.`];
			if (skipped.length > 0) {
				const reasons = [...new Set(skipped.map(s => s.reason))].join(', ');
				lines.push(`Skipped ${skipped.length} (${reasons}).`);
			}
			if (failed.length > 0) {
				const failedNames = failed.map(f => nameFor(f.id));
				const shown = failedNames.slice(0, 8).join(', ') + (failedNames.length > 8 ? '…' : '');
				lines.push(`Couldn't update ${failed.length} (${shown}) — you may not have permission for those.`);
			}
			alert(lines.join('\n'));
		} catch (error) {
			console.error('Error performing batch action:', error);
			alert('Something went wrong updating those students. Please try again.');
		}
	};

	useEffect(() => {
		let filtered = students.filter((student) => {
			// Search filter
			const searchLower = searchTerm.toLowerCase();
			const matchesSearch =
				!searchTerm ||
				student.fullName?.toLowerCase().includes(searchLower) ||
				student.email?.toLowerCase().includes(searchLower) ||
				student.firstName?.toLowerCase().includes(searchLower) ||
				student.lastName?.toLowerCase().includes(searchLower) ||
				student.school?.toLowerCase().includes(searchLower);

			// Status filter
			const matchesStatus =
				statusFilter === 'all' ||
				(statusFilter === 'active' && student.status === 'Active') ||
				(statusFilter === 'inactive' && student.status !== 'Active');

			// Day filter
			const matchesDay =
				dayFilter === 'all' ||
				student.lessonDay === dayFilter;

			// Student type filter
			const matchesType =
				studentTypeFilter === 'all' ||
				(studentTypeFilter === 'real' && !student.isTestStudent) ||
				(studentTypeFilter === 'test' && student.isTestStudent === true);

			return matchesSearch && matchesStatus && matchesDay && matchesType;
		});

		// Apply sorting
		filtered = sortStudents(filtered);

		setFilteredStudents(filtered);
	}, [searchTerm, students, sortConfig, statusFilter, dayFilter, studentTypeFilter]);

	// Sortable header cell.
	//
	// The click target is a real <button>, matching the Slot Editor and Inquiries
	// (it was an onClick on the <th>, which no keyboard could reach). Under
	// .ps-table--fit it also has to be a button: the label needs its own element
	// to ellipsize in, and `aria-sort` needs somewhere honest to live now that
	// columns are width-capped and headers can truncate.
	const SortTh = ({ col, label, className = '' }) => {
		const active = sortConfig.key === col;
		return (
			<th
				className={`${className} ps-sortable`.trim()}
				aria-sort={active ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
				<button type='button' className='ps-sort-btn' title={label} onClick={() => handleSort(col)}>
					<span className='ps-sort-btn__label'>{label}</span>
					<i
						className={`fa-sharp fa-solid ${
							active
								? sortConfig.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'
								: 'fa-arrows-up-down ps-sort-btn__idle'
						}`}
						aria-hidden='true'
					/>
				</button>
			</th>
		);
	};

	const goTo = (id) => navigate(`/portal/work/students/${id}`);
	const dash = <span className='text-muted'>—</span>;

	if (loading) {
		return (
			<>
				<PortalHeader title="Students" subtitle="View and manage your student roster" />
				<TestModeBanner />
				<div className="ps-page">
					<div className="ps-spinner" role="status" aria-label="Loading" />
				</div>
			</>
		);
	}

	return (
		<>
			<PortalHeader title="Students" subtitle="View and manage your student roster" />
			<TestModeBanner />
			<div className="ps-page students-page">
				{/* Filters */}
				<div className="ps-card students-filters">
					<div className="students-filters__row">
						<div className="ps-search students-filters__search">
							<i className="fa-sharp fa-solid fa-search" />
							<input
								type="text"
								className="ps-input"
								placeholder="Search by name, email, or school…"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
						</div>
						<select
							className="ps-select"
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}>
							<option value="all">All Status</option>
							<option value="active">Active Only</option>
							<option value="inactive">Inactive Only</option>
						</select>
						<select
							className="ps-select"
							value={studentTypeFilter}
							onChange={(e) => setStudentTypeFilter(e.target.value)}>
							<option value="all">All Students</option>
							<option value="real">Real Only</option>
							<option value="test">Test Only</option>
						</select>
						<select
							className="ps-select"
							value={dayFilter}
							onChange={(e) => setDayFilter(e.target.value)}>
							<option value="all">All Days</option>
							<option value="Monday">Monday</option>
							<option value="Tuesday">Tuesday</option>
							<option value="Wednesday">Wednesday</option>
							<option value="Thursday">Thursday</option>
							<option value="Friday">Friday</option>
							<option value="Saturday">Saturday</option>
							<option value="Sunday">Sunday</option>
						</select>
						<details className="ps-menu">
							<summary className="ps-btn">
								<i className="fa-sharp fa-solid fa-table-columns" /> Columns
							</summary>
							<div className="ps-menu__panel">
								<div className="ps-menu__header">Show / hide columns</div>
								{Object.keys(COLUMN_LABELS).map((col) => (
									<label className="ps-menu__item" key={col}>
										<input
											type="checkbox"
											checked={visibleColumns[col]}
											onChange={() => toggleColumn(col)}
										/>
										{COLUMN_LABELS[col]}
									</label>
								))}
							</div>
						</details>
					</div>
					<div className="students-filters__meta">
						<small className="text-muted">
							Showing {filteredStudents.length} of {students.length} students
						</small>
						<small className="text-muted">
							Sorted by: {sortConfig.key} ({sortConfig.direction})
						</small>
					</div>
				</div>

				{/* Batch Actions Toolbar */}
				{selectedStudents.length > 0 && (
					<div className="ps-selectbar">
						<div>
							<i className="fa-sharp fa-solid fa-square-check me-2" />
							<strong>{selectedStudents.length}</strong> student{selectedStudents.length > 1 ? 's' : ''} selected
						</div>
						<div className="ps-selectbar__actions">
							{/* Portal invites are sendEmail-gated at the callable
							    (2026-07-04); hide for staff without the grant (#22). */}
							{canSendEmail && (
								<button className="btn-cyv-teal students-sm-btn" onClick={() => setShowPortalInvite(true)}>
									<i className="fa-sharp fa-solid fa-paper-plane" /> Send Portal Access
								</button>
							)}
							<button className="ps-btn" onClick={() => setShowBatchModal(true)}>
								<i className="fa-sharp fa-solid fa-pen-to-square" /> Batch Edit
							</button>
							<button
								className="ps-btn"
								onClick={() => {
									setSelectedStudents([]);
									setSelectAll(false);
								}}>
								<i className="fa-sharp fa-solid fa-xmark" /> Clear Selection
							</button>
						</div>
					</div>
				)}

				{permDenied ? (
					<AccessNotice what="the student roster" />
				) : filteredStudents.length === 0 ? (
					<div className="ps-alert ps-alert--info">
						{searchTerm
							? 'No students found matching your search.'
							: 'No students registered yet.'}
					</div>
				) : (
					<div className="ps-table-wrap">
						<table className="ps-table students-table ps-table--fit">
							<thead>
								<tr>
									{/* The 42px inline width was removed with the fit change: mixing a
									    px column with percentage ones under table-layout:fixed
									    over-constrains the row, so the checkbox gets a share like
									    everything else (see Students.css). */}
									<th className="col--select">
										<input
											type="checkbox"
											checked={selectAll}
											onChange={handleSelectAll}
											aria-label="Select all"
										/>
									</th>
									{visibleColumns.name && <SortTh col="fullName" label="Name" className="col--name" />}
									{visibleColumns.email && <SortTh col="email" label="Email" className="col--email" />}
									{visibleColumns.phone && <th className="col--phone">Phone</th>}
									{visibleColumns.school && <SortTh col="school" label="School" className="col--school" />}
									{visibleColumns.age && <SortTh col="age" label="Age" className="col--age" />}
									{visibleColumns.instrument && <th className="col--instrument">Instrument</th>}
									{visibleColumns.parent && <th className="col--parent">Parent Contact</th>}
									{visibleColumns.status && <SortTh col="status" label="Status" className="col--status" />}
									{visibleColumns.portalAccess && <SortTh col="portalAccess" label="Portal Access" className="col--portalaccess" />}
									{visibleColumns.nextLesson && <SortTh col="nextLesson" label="Next Lesson" className="col--nextlesson" />}
									{visibleColumns.lessonDay && <SortTh col="lessonDay" label="Lesson Day" className="col--lessonday" />}
								</tr>
							</thead>
							<tbody>
								{filteredStudents.map((student) => (
									<tr key={student.id}>
										<td className="col--select" onClick={(e) => e.stopPropagation()}>
											<input
												type="checkbox"
												checked={selectedStudents.includes(student.id)}
												onChange={() => handleSelectStudent(student.id)}
												aria-label={`Select ${student.fullName || 'student'}`}
											/>
										</td>
										{visibleColumns.name && (
											<td className="students-link col--name" onClick={() => goTo(student.id)}>
												<span className="ps-clip" title={student.fullName || `${student.firstName} ${student.lastName}`}>
												<span className="ps-cell-strong">
													{student.fullName || `${student.firstName} ${student.lastName}`}
												</span>
												{student.locked && (
													<span className="ps-badge ps-badge--neutral ms-2" title="This student is locked and cannot be edited">
														<i className="fa-sharp fa-solid fa-lock" /> Locked
													</span>
												)}
												{student.isTestStudent && (
													<span className="ps-badge ps-badge--info ms-2" title="Test student for development">
														<i className="fa-sharp fa-solid fa-flask" /> Test
													</span>
												)}
												</span>
											</td>
										)}
										{visibleColumns.email && (
											<td className="students-link col--email" onClick={() => goTo(student.id)}>
												<span className="ps-clip" title={student.email || ''}>{student.email || dash}</span>
											</td>
										)}
										{visibleColumns.phone && (
											<td className="students-link col--phone" onClick={() => goTo(student.id)}>
												<span className="ps-clip" title={student.phone || student.parent1?.phone || ''}>{student.phone || student.parent1?.phone || dash}</span>
											</td>
										)}
										{visibleColumns.school && (
											<td className="students-link col--school" onClick={() => goTo(student.id)}>
												<span className="ps-clip" title={student.school || ''}>{student.school || dash}</span>
											</td>
										)}
										{visibleColumns.age && (
											<td className="students-link col--age" onClick={() => goTo(student.id)}>
												{student.age || dash}
											</td>
										)}
										{visibleColumns.instrument && (
											<td className="students-link col--instrument" onClick={() => goTo(student.id)}>
												<span className="ps-clip" title={student.instrument || ''}>{student.instrument || dash}</span>
											</td>
										)}
										{visibleColumns.parent && (
											<td className="students-link col--parent" onClick={() => goTo(student.id)}>
												{student.parent1?.firstName ? (
													<span className="ps-clip" title={`${student.parent1.firstName} ${student.parent1.lastName}`.trim()}>
														{student.parent1.firstName} {student.parent1.lastName}
														<div className="ps-cell-sub">{student.parent1.email}</div>
														{student.parent1.phone && (
															<div className="ps-cell-sub">{student.parent1.phone}</div>
														)}
													</span>
												) : dash}
											</td>
										)}
										{visibleColumns.status && (
											<td className="students-link col--status" onClick={() => goTo(student.id)}>
												<span className={`ps-badge ps-badge--${student.status === 'Active' ? 'success' : 'neutral'}`}>
													{student.status || 'Active'}
												</span>
												{student.archived && (
													<span className="ps-badge ps-badge--neutral ms-1">
														<i className="fa-sharp fa-solid fa-box-archive" /> Archived
													</span>
												)}
												{student.accountBalance !== undefined && student.accountBalance < 0 && (
													<span className="ps-badge ps-badge--danger ms-1" title={`Owes $${Math.abs(student.accountBalance).toFixed(2)}`}>
														<i className="fa-sharp fa-solid fa-circle-exclamation" /> Balance Due
													</span>
												)}
											</td>
										)}
										{visibleColumns.portalAccess && (
											<td className="students-link col--portalaccess" onClick={() => goTo(student.id)}>
												{student.portalAccess ? (
													<span
														className={`ps-badge ps-badge--${student.portalAccess.variant}`}
														title={portalAccessDetail(student.portalAccess)}>
														{student.portalAccess.label}
													</span>
												) : (
													<span className="text-muted" title="Portal invite status could not be read">{dash}</span>
												)}
											</td>
										)}
										{visibleColumns.nextLesson && (
											<td className="students-link col--nextlesson" onClick={() => goTo(student.id)}>
												{nextLessonDate(student.nextLesson) ? nextLessonDate(student.nextLesson).toLocaleDateString() : dash}
											</td>
										)}
										{visibleColumns.lessonDay && (
											<td className="students-link col--lessonday" onClick={() => goTo(student.id)}>
												{student.lessonDay ? (
													<span className="ps-badge" style={getDayBadgeStyle(student.lessonDay)}>
														{student.lessonDay}
													</span>
												) : dash}
											</td>
										)}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}

				{/* Batch Edit Modal */}
				{showBatchModal && (
					<div className="ps-modal-overlay" onClick={() => setShowBatchModal(false)}>
						<div className="ps-modal" onClick={(e) => e.stopPropagation()}>
							<h3 className="ps-modal__title">Batch edit students</h3>
							<p>
								Apply changes to <strong>{selectedStudents.length}</strong> selected student{selectedStudents.length > 1 ? 's' : ''}:
							</p>
							<div className="ps-field">
								<label className="ps-label" htmlFor="batch-action">Select action</label>
								<select
									id="batch-action"
									className="ps-select"
									value={batchAction}
									onChange={(e) => setBatchAction(e.target.value)}>
									<option value="">Choose an action…</option>
									<optgroup label="Status">
										<option value="markActive">Mark as Active</option>
										<option value="markInactive">Mark as Inactive</option>
									</optgroup>
									<optgroup label="Student Type">
										<option value="markReal">Mark as Real Student</option>
										<option value="markTest">Mark as Test Student</option>
									</optgroup>
									<optgroup label="Protection">
										<option value="lock">Lock Students (prevent edits)</option>
										<option value="unlock">Unlock Students</option>
									</optgroup>
									<optgroup label="Archive">
										<option value="archive">Archive Students (inactive + no balance)</option>
										<option value="unarchive">Unarchive Students</option>
									</optgroup>
								</select>
							</div>
							{batchAction && (
								<div className={`ps-alert ${batchAction === 'lock' || batchAction === 'archive' ? 'ps-alert--info' : 'ps-alert--danger'}`} style={{ marginTop: 'var(--space-3)' }}>
									<i className="fa-sharp fa-solid fa-triangle-exclamation me-2" />
									{batchAction === 'lock' && (
										<><strong>Lock protection:</strong> Locked students cannot be edited or marked inactive until unlocked. This protects real student data during development.</>
									)}
									{batchAction === 'unlock' && (
										<><strong>Unlock students:</strong> This will allow selected students to be edited again.</>
									)}
									{batchAction === 'archive' && (
										<><strong>Archive requirements:</strong> Only <strong>inactive</strong> students with <strong>zero or positive balance</strong> will be archived. Students with outstanding balances will be skipped.</>
									)}
									{batchAction === 'unarchive' && (
										<><strong>Unarchive:</strong> This will restore selected students from the archive.</>
									)}
									{!['lock', 'unlock', 'archive', 'unarchive'].includes(batchAction) && (
										<>This will update all selected students. Locked students will be skipped for status changes.</>
									)}
								</div>
							)}
							<div className="ps-modal__footer">
								<button className="btn-cyv-ghost" onClick={() => setShowBatchModal(false)}>Cancel</button>
								<button className="btn-cyv-teal" onClick={handleBatchAction} disabled={!batchAction}>
									Apply changes
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Send Portal Invite Modal (shared Bootstrap component — deferred) */}
				<SendPortalInvite
					show={showPortalInvite}
					onHide={() => setShowPortalInvite(false)}
					students={filterAvailableStudents(students.filter(s => selectedStudents.includes(s.id)))}
					onSuccess={(results) => {
						console.log('Portal invites sent:', results);
						setShowPortalInvite(false);
						// Optionally clear selection after sending
						setSelectedStudents([]);
						setSelectAll(false);
					}}
				/>
			</div>
		</>
	);
};

export default Students;
